import type {
	FinancialPropertyListItem,
	ServicePeriodListItem,
	ServicePeriodStatusValue,
	UnitPayoutStatusValue,
} from "@/features/financialShared";
import {
	ServicePeriodStatus,
	UnitPayoutStatus,
} from "@/features/financialShared";
import type {
	RevenuePeriodUnitsHeader,
	RevenuePeriodUnitsStats,
	RevenueUnitListItem,
} from "./revenueManagement.types";

export interface RevenueApproverOption {
	id: string;
	name: string;
}

export interface RevenueManagementPermissions {
	canEdit: boolean;
	canPublish: boolean;
}

export interface GetRevenueManagementPropertiesArgs {
	status?: string;
	sortBy?: string;
	sortOrder?: "ASC" | "DESC";
	page?: number;
	pageSize?: number;
	search?: string;
	approverId?: string;
}

export interface RevenueManagementPropertiesResponse {
	pageTitle: string;
	lastSyncLabel: string;
	approverOptions: RevenueApproverOption[];
	items: FinancialPropertyListItem[];
	page: number;
	pageSize: number;
	total: number;
	totalPages: number;
	hasPrevious: boolean;
	hasNext: boolean;
	permissions: RevenueManagementPermissions;
}

export interface GetRevenueServicePeriodsArgs {
	propertyId: string;
	page?: number;
	pageSize?: number;
	search?: string;
	status?: string;
	approverId?: string;
}

export interface RevenueServicePeriodsResponse {
	propertyId: string;
	propertyName: string;
	unitCount: number;
	items: ServicePeriodListItem[];
	approverOptions: RevenueApproverOption[];
	page: number;
	pageSize: number;
	total: number;
	totalPages: number;
	emptyMessage: string;
}

export interface RevenuePeriodFileBody {
	filePath: string;
	fileName: string;
	mimeType: string;
	fileSize: number;
}

export interface CreateRevenueServicePeriodBody {
	periodStart: string;
	periodEnd: string;
	file?: RevenuePeriodFileBody;
}

export interface GetRevenueServicePeriodUnitsArgs {
	propertyId: string;
	periodId: string;
	page?: number;
	pageSize?: number;
	search?: string;
	status?: string;
	payoutMethod?: string;
	sortBy?: string;
	sortOrder?: "ASC" | "DESC";
}

export interface RevenueServicePeriodUnitsResponse {
	header: RevenuePeriodUnitsHeader;
	stats: RevenuePeriodUnitsStats;
	items: RevenueUnitListItem[];
	page: number;
	pageSize: number;
	total: number;
	totalPages: number;
}

export type RevenueUnitsExportScope = "ALL" | "PENDING";

export interface ExportRevenueServicePeriodUnitsArgs {
	propertyId: string;
	periodId: string;
	scope: RevenueUnitsExportScope;
}

export interface UploadRevenueServicePeriodUnitsArgs {
	propertyId: string;
	periodId: string;
	body: { file: RevenuePeriodFileBody };
}

export interface RevenueUnitPathArgs {
	propertyId: string;
	periodId: string;
	unitId: string;
}

export interface UpdateRevenueUnitStatementArgs {
	propertyId: string;
	periodId: string;
	unitId: string;
	body: UpdateRevenueUnitStatementBody;
}

export interface UpdateRevenueUnitStatementBody {
	expectedUpdatedAt?: string;
	statement: {
		statementDate: string;
		payoutDate: string;
		status: string;
	};
	payout: {
		payoutMethod: string;
		payoutRefId: string;
		netRevenue: number;
		ownerShare: number;
		balanceToPay: number;
		note: string;
	};
	unitType: string;
	ownerInvoiceDetails: Array<{
		ownerName: string;
		unit: string;
		invoiceNumber: string;
		startDate: string;
		endDate: string;
	}>;
	activityDetail: Array<{
		period: string;
		unit: string;
		unitType: string;
		arrivalDate: string;
		departureDate: string;
		reservationNights: number;
		netRoomRevenue: number;
	}>;
	activitySummary: Array<{
		itemName: string;
		itemKey: string;
		isDeduction: boolean;
		isTotal: boolean;
		values: Array<{ period: string; amount: number }>;
	}>;
}

export type { DownloadedFile } from "@/features/financialShared";

export const REVENUE_STATUS_API: Record<string, string> = {
	[ServicePeriodStatus.DRAFT]: "DRAFT",
	[ServicePeriodStatus.PUBLISHED]: "PUBLISHED",
	[ServicePeriodStatus.COMPLETED]: "COMPLETED",
};

export function mapRevenueStatusFromApi(
	raw: unknown,
): ServicePeriodStatusValue | null {
	const value = String(raw ?? "")
		.trim()
		.toUpperCase();
	if (value === "DRAFT") return ServicePeriodStatus.DRAFT;
	if (value === "PUBLISHED") return ServicePeriodStatus.PUBLISHED;
	if (value === "COMPLETED") return ServicePeriodStatus.COMPLETED;
	return null;
}

export const REVENUE_UNIT_STATUS_API: Record<string, string> = {
	[UnitPayoutStatus.SETTLED]: "SETTLED",
	[UnitPayoutStatus.IN_PROGRESS]: "IN_PROGRESS",
	[UnitPayoutStatus.HOLD]: "HOLD",
	[UnitPayoutStatus.PARTIALLY_PAID]: "PARTIALLY_PAID",
	[UnitPayoutStatus.PAST_DUE]: "PAST_DUE",
	[UnitPayoutStatus.OUTSTANDING]: "OUTSTANDING",
	[UnitPayoutStatus.CREDIT]: "CREDIT",
};

export function mapRevenueUnitStatusFromApi(
	raw: unknown,
): UnitPayoutStatusValue {
	const value = String(raw ?? "")
		.trim()
		.toUpperCase()
		.replace(/[\s-]+/g, "_");
	if (value === "SETTLED") return UnitPayoutStatus.SETTLED;
	if (value === "IN_PROGRESS") return UnitPayoutStatus.IN_PROGRESS;
	if (value === "HOLD") return UnitPayoutStatus.HOLD;
	if (value === "PARTIALLY_PAID") return UnitPayoutStatus.PARTIALLY_PAID;
	if (value === "PAST_DUE") return UnitPayoutStatus.PAST_DUE;
	if (value === "OUTSTANDING") return UnitPayoutStatus.OUTSTANDING;
	if (value === "CREDIT") return UnitPayoutStatus.CREDIT;
	return UnitPayoutStatus.IN_PROGRESS;
}

export const REVENUE_PAYOUT_METHOD_API: Record<string, string> = {
	"Bank Transfer": "BANK_TRANSFER",
	Card: "CARD",
	Cheque: "CHEQUE",
};

export function mapRevenuePayoutMethodToApi(
	raw: unknown,
): "BANK_TRANSFER" | "CARD" | "CHEQUE" | "" {
	const value = String(raw ?? "")
		.trim()
		.toUpperCase()
		.replace(/[\s-]+/g, "_");
	if (value === "BANK_TRANSFER") return "BANK_TRANSFER";
	if (value === "CARD") return "CARD";
	if (value === "CHEQUE" || value === "CHECK") return "CHEQUE";
	return "";
}

export function mapRevenuePayoutMethodLabel(raw: unknown): string {
	const value = mapRevenuePayoutMethodToApi(raw);
	if (value === "BANK_TRANSFER") return "Bank Transfer";
	if (value === "CARD") return "Card";
	if (value === "CHEQUE") return "Cheque";
	if (typeof raw === "string" && raw.trim()) return raw.trim();
	return "—";
}
